import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ImageUp, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, unwrap } from '@/lib/api/client';
import { cn } from '@/lib/utils';

/**
 * Pick an image, or paste the URL of one.
 *
 * `POST utility/{container}/uploadfile` takes a multipart `file` and answers
 * with the stored URL as a plain string. The container is a path segment, so
 * each caller names its own — `infocard`, `bannerad`, and so on.
 *
 * Both halves are kept because the original had both and they answer different
 * situations: marketing uploads a new asset, or points at one already hosted.
 * The value is always just a URL, so nothing downstream needs to know which
 * route it arrived by.
 */

/** Anything larger is a mistake rather than a choice, and the drop is refused. */
const MAX_BYTES = 5 * 1024 * 1024;

const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function useUploadImage(container: string) {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post(`utility/${container}/uploadfile`, body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}

export function ImageUploadField({
  value,
  onChange,
  container,
  label,
  hint,
  disabled,
}: {
  value: string;
  onChange: (url: string) => void;
  /** Storage container the file lands in, e.g. `infocard`. */
  container: string;
  label: string;
  hint?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const upload = useUploadImage(container);
  const locked = disabled || upload.isPending;

  function accept(file: File | undefined) {
    setProblem(null);
    if (!file) return;

    // Checked here rather than left to the server, which answers 200 with an
    // error string and would otherwise look like a silent no-op.
    if (!ACCEPTED.includes(file.type)) {
      setProblem(t('imageUpload.wrongType'));
      return;
    }
    if (file.size > MAX_BYTES) {
      setProblem(t('imageUpload.tooLarge'));
      return;
    }

    upload.mutate(file, {
      onSuccess: onChange,
      onError: (error) =>
        setProblem(
          error instanceof Error ? error.message : t('imageUpload.failed')
        ),
    });
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId} className="text-xs font-semibold">
        {label}
      </Label>

      <Input
        id={inputId}
        value={value}
        disabled={locked}
        placeholder={t('imageUpload.urlPlaceholder')}
        onChange={(event) => onChange(event.target.value)}
      />

      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!locked) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!locked) accept(event.dataTransfer.files[0]);
        }}
        className={cn(
          'rounded-xl border border-dashed p-4 text-center transition-colors',
          dragging && 'border-primary bg-primary/5',
          locked && 'opacity-60'
        )}
      >
        {value ? (
          <div className="space-y-2">
            <img
              src={value}
              alt=""
              // Contained rather than cropped: marketing is checking framing,
              // and a cover fit would hide exactly what they are checking.
              className="bg-muted mx-auto max-h-32 w-auto rounded-lg object-contain"
              onError={(event) => {
                event.currentTarget.style.display = 'none';
              }}
            />
            <div className="flex justify-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={locked}
                onClick={() => fileInput.current?.click()}
              >
                {t('imageUpload.replace')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={locked}
                onClick={() => onChange('')}
              >
                <X className="size-3.5" />
                {t('imageUpload.clear')}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={locked}
            onClick={() => fileInput.current?.click()}
            className="text-muted-foreground hover:text-foreground flex w-full flex-col items-center gap-1.5 text-sm"
          >
            {upload.isPending ? (
              <Loader2 className="size-6 animate-spin" />
            ) : (
              <ImageUp className="size-6" />
            )}
            <span>{t('imageUpload.prompt')}</span>
          </button>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept={ACCEPTED.join(',')}
        className="hidden"
        onChange={(event) => {
          accept(event.target.files?.[0]);
          // Cleared so picking the same file twice still fires a change.
          event.target.value = '';
        }}
      />

      {problem && <p className="text-destructive text-xs">{problem}</p>}
      {hint && !problem && (
        <p className="text-muted-foreground text-xs">{hint}</p>
      )}
    </div>
  );
}
